/**
 * The plans for the chart you are looking at — list, new, edit, delete.
 *
 * The missing half of the journal. `PlanForm` shipped mounted and working, so a
 * plan could be *armed* and then never seen again: nothing listed it, nothing
 * drew it, and the only feedback that a save had worked was the dialog closing.
 * That reads exactly like a broken button, and it is what this panel fixes.
 *
 * ## Scoped to the active chart's symbol
 *
 * A plan belongs to a symbol, and the chart already says which symbol you are
 * thinking about. Listing the whole journal here would put twenty rows in a
 * 280px popover to show the one that matters, and the whole-journal view is a
 * different screen with different columns (equity, R distribution, review
 * notes) rather than a taller version of this one.
 *
 * ## Deleting is offered only where it is honest
 *
 * A plan that has filled has a trade and executions behind it, and the server
 * refuses to delete it — correctly, since removing it would silently drop the
 * record of a position that existed. So the button is not shown for those, and
 * the refusal never has to be explained after the fact.
 */

import { useCallback, useEffect, useState } from "react";
import type { Execution, Plan, Trade } from "@qufolio/model";

import { PlanList } from "./PlanList";
import { deletePlan, fetchJournal } from "../api/journal";

export interface PlanPanelProps {
  symbol: string;
  /** Open the form to create a plan, pre-filled from the chart. */
  onNew: () => void;
  /** Open the form on an existing plan. */
  onEdit: (plan: Plan) => void;
  /** Bumped by the caller after a save, so the list re-reads. */
  reloadKey: number;
  /**
   * The journal changed here — the host must re-read it too.
   *
   * This panel reloading itself is not enough: the chart draws its levels from
   * the host's copy, so a plan deleted here stayed on the chart, undeletable,
   * because nothing told the thing that was drawing it.
   */
  onChanged?: () => void;
  onClose: () => void;
}

export function PlanPanel({
  symbol,
  onNew,
  onEdit,
  reloadKey,
  onChanged,
  onClose,
}: PlanPanelProps) {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [executions, setExecutions] = useState<Execution[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      // The whole journal rather than `/journal/plans/{symbol}`, because the
      // list shows realised R — which needs the trades and executions, and
      // those have no per-symbol endpoint. Ship one is hundreds of rows.
      const journal = await fetchJournal();
      setPlans(journal.plans ?? []);
      setTrades(journal.trades ?? []);
      setExecutions(journal.executions ?? []);
      setError(null);
    } catch (cause) {
      // Shown, never swallowed: an empty list and a failed fetch look identical
      // otherwise, and "you have no plans" is the more believable of the two.
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, reloadKey]);

  const mine = plans.filter((plan) => plan.symbol.toUpperCase() === symbol.toUpperCase());

  const remove = async (plan: Plan) => {
    try {
      await deletePlan(plan.id);
      await load();
      // The chart draws from the host's copy of the journal, not this one.
      onChanged?.();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  };

  return (
    <div style={PANEL}>
      <div style={HEAD}>
        <strong style={{ fontSize: 12 }}>Plans · {symbol}</strong>
        <div style={{ display: "flex", gap: 4 }}>
          <button type="button" onClick={onNew} style={NEW}>
            New
          </button>
          <button type="button" onClick={onClose} aria-label="Close plans" style={CLOSE}>
            ×
          </button>
        </div>
      </div>

      {error && <div style={ERROR}>{error}</div>}
      {busy && mine.length === 0 && <div style={MUTED}>Loading…</div>}

      <div style={{ maxHeight: 320, overflowY: "auto" }}>
        <PlanList plans={mine} trades={trades} executions={executions} onPick={onEdit} />
      </div>

      {mine.length > 0 && (
        <div style={FOOT}>
          {mine.map((plan) => (
            <div key={plan.id} style={ROW}>
              <span style={{ color: "var(--qf-text-secondary)" }}>
                {plan.direction === "short" ? "Short" : "Long"} @ {plan.entry}
              </span>
              <span style={{ display: "flex", gap: 4 }}>
                <button type="button" onClick={() => onEdit(plan)} style={LINK}>
                  Edit
                </button>
                {/* Only where the server would allow it — see the docstring. */}
                {(plan.status === "draft" ||
                  plan.status === "armed" ||
                  plan.status === "cancelled") && (
                  <button type="button" onClick={() => void remove(plan)} style={LINK}>
                    Delete
                  </button>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const PANEL: React.CSSProperties = {
  width: 300,
  background: "var(--qf-surface)",
  border: "1px solid var(--qf-border)",
  borderRadius: 6,
  boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
  color: "var(--qf-text)",
  fontSize: 12,
};

const HEAD: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "6px 8px",
  borderBottom: "1px solid var(--qf-border)",
};

const NEW: React.CSSProperties = {
  padding: "2px 8px",
  background: "var(--qf-accent)",
  border: "none",
  borderRadius: 4,
  color: "#fff",
  cursor: "pointer",
  font: "inherit",
};

const CLOSE: React.CSSProperties = {
  width: 20,
  height: 20,
  padding: 0,
  background: "transparent",
  border: "none",
  borderRadius: 4,
  color: "var(--qf-text-secondary)",
  cursor: "pointer",
  font: "inherit",
  fontSize: 14,
};

const LINK: React.CSSProperties = {
  padding: 0,
  background: "transparent",
  border: "none",
  color: "var(--qf-accent)",
  cursor: "pointer",
  font: "inherit",
  textDecoration: "underline",
};

const ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "3px 8px",
};

const FOOT: React.CSSProperties = {
  borderTop: "1px solid var(--qf-border)",
  padding: "4px 0",
};

const MUTED: React.CSSProperties = { padding: 12, color: "var(--qf-text-muted)" };

const ERROR: React.CSSProperties = {
  padding: "6px 8px",
  color: "var(--qf-negative, #e5484d)",
  borderBottom: "1px solid var(--qf-border)",
};
